import { RESERVED_KEYS } from './constants';
import type { KeyboardShortcut, KeyboardShortcutsOptions } from './types';

const isTypingTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  return (
    target.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
  );
};

/**
 * Returns the `KeyboardEvent.code` for the physical key that types `key`.
 * Letters map to `KeyA`–`KeyZ` and digits to `Digit0`–`Digit9`. Named keys such
 * as Escape, Delete and ArrowUp are already valid codes and are returned as-is.
 *
 * @param key - A `KeyboardEvent.key` value from a shortcut binding.
 * @returns The matching `KeyboardEvent.code`.
 */
const toKeyCode = (key: string): string => {
  if (/^[a-z]$/i.test(key)) {
    return `Key${key.toUpperCase()}`;
  }

  if (/^[0-9]$/.test(key)) {
    return `Digit${key}`;
  }

  return key;
};

const matchesKey = (
  shortcut: KeyboardShortcut,
  event: KeyboardEvent,
): boolean => {
  const key = event.key.toLowerCase();

  return shortcut.keys.some(
    (shortcutKey) =>
      shortcutKey.toLowerCase() === key ||
      // Mac rewrites `key` for alt combinations, so fall back to the physical
      // key. Only where it is needed: matching on `code` everywhere would tie
      // every shortcut to the QWERTY layout.
      (event.altKey && toKeyCode(shortcutKey) === event.code),
  );
};

/**
 * Returns whether the event holds exactly the modifiers the shortcut declares.
 * Ctrl and Cmd are treated as interchangeable so one binding covers Windows and
 * Mac.
 *
 * @param shortcut - The shortcut whose modifiers to check.
 * @param event - The keydown event to compare against.
 * @returns `true` when the held modifiers match the shortcut exactly.
 */
const matchesModifiers = (
  shortcut: KeyboardShortcut,
  event: KeyboardEvent,
): boolean =>
  !!shortcut.withCommand === (event.metaKey || event.ctrlKey) &&
  !!shortcut.withShift === event.shiftKey &&
  !!shortcut.withAlt === event.altKey;

/**
 * Returns the first enabled shortcut that matches the keydown, or `null` when
 * the event should not be handled: the set is disabled, the key is auto-repeating,
 * the user is typing in a field, or no binding matches.
 *
 * Kept free of React so the matching guards can be unit-tested directly.
 *
 * @param event - The keydown event to resolve.
 * @param shortcuts - Bindings to search.
 * @param options - Options applying to the whole set.
 * @returns The matching shortcut, or `null`.
 */
export const resolveShortcut = (
  event: KeyboardEvent,
  shortcuts: KeyboardShortcut[],
  options: KeyboardShortcutsOptions = {},
): KeyboardShortcut | null => {
  const { enabled = true } = options;

  if (!enabled || event.repeat || isTypingTarget(event.target)) {
    return null;
  }

  const match = shortcuts.find(
    (shortcut) =>
      shortcut.enabled !== false &&
      matchesModifiers(shortcut, event) &&
      matchesKey(shortcut, event),
  );

  return match ?? null;
};

/**
 * Returns a keydown listener that resolves the current shortcuts and, on a match,
 * prevents the default browser action, stops propagation, and runs the handler.
 *
 * Shortcuts and options are read through getters so the listener always sees the
 * latest values without being recreated.
 *
 * @param getShortcuts - Returns the current shortcut bindings.
 * @param getOptions - Returns the current options for the set.
 * @returns A `keydown` listener to attach to `window`.
 */
export const createShortcutListener =
  (
    getShortcuts: () => KeyboardShortcut[],
    getOptions: () => KeyboardShortcutsOptions,
  ) =>
  (event: KeyboardEvent): void => {
    const shortcut = resolveShortcut(event, getShortcuts(), getOptions());

    if (!shortcut) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    shortcut.handler();
  };

const hasModifier = (shortcut: KeyboardShortcut): boolean =>
  !!shortcut.withCommand || !!shortcut.withShift || !!shortcut.withAlt;

/**
 * Returns the unmodified shortcut keys that collide with keys already used by
 * the 3D map. Shortcuts that require a modifier are excluded, because those do
 * not overlap with the map's bare-key bindings.
 *
 * @param shortcuts - Bindings to check for collisions.
 * @returns The colliding key strings.
 */
export const findReservedKeyCollisions = (
  shortcuts: KeyboardShortcut[],
): string[] =>
  shortcuts
    .filter((shortcut) => !hasModifier(shortcut))
    .flatMap((shortcut) => shortcut.keys)
    .filter((key) =>
      RESERVED_KEYS.some(
        (reservedKey) => reservedKey.toLowerCase() === key.toLowerCase(),
      ),
    );
